import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import SiteShell from '@/components/feature/SiteShell';
import MovieCard from '@/components/base/MovieCard';
import { usePageMeta } from '@/hooks/usePageMeta';
import { hotMovies } from '@/lib/catalog';

export default function NotFound() {
  const location = useLocation();
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState('');

  usePageMeta({
    title: '页面不存在 - 黄牛视频',
    description: '你要访问的页面不存在或已移动。可以返回黄牛视频首页，或搜索想看的影视作品。',
    keywords: '404,页面不存在,黄牛视频',
  });

  return (
    <SiteShell>
      <section className="w-full px-4 pb-10 pt-12 md:px-8 md:pb-14 md:pt-16">
        <div className="mx-auto max-w-3xl text-center">
          <p className="font-label text-[64px] font-extrabold leading-none tracking-tight text-primary-100 md:text-[104px]">
            404
          </p>
          <h1 className="mt-2 font-heading text-2xl font-extrabold tracking-tight text-foreground-950 md:text-3xl">
            这个页面走丢了
          </h1>
          <p className="mx-auto mt-3 max-w-lg text-sm leading-relaxed text-foreground-600">
            你访问的地址可能已经变更或被移除。可以回到首页继续浏览，也可以直接搜索想看的影视作品。
          </p>
          <p className="mt-2 font-label text-[11px] text-foreground-400">当前路径：{location.pathname}</p>

          <form
            className="mx-auto mt-6 flex max-w-md flex-col gap-2 sm:flex-row"
            onSubmit={(event) => {
              event.preventDefault();
              const query = keyword.trim();
              navigate(query ? `/search?q=${encodeURIComponent(query)}` : '/search');
            }}
          >
            <div className="flex flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2.5 focus-within:border-primary-300">
              <i className="ri-search-line text-sm text-foreground-400" />
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索影视作品"
                aria-label="搜索影视作品"
                className="w-full bg-transparent text-sm outline-none placeholder:text-foreground-400"
              />
            </div>
            <button
              type="submit"
              className="cursor-pointer whitespace-nowrap rounded-md bg-primary-900 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-800"
            >
              搜索
            </button>
          </form>

          <div className="mt-5 flex flex-wrap justify-center gap-2">
            <Link
              to="/"
              className="whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-xs text-foreground-700 transition-colors hover:bg-background-100"
            >
              返回首页
            </Link>
            <Link
              to="/category/movie"
              className="whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-xs text-foreground-700 transition-colors hover:bg-background-100"
            >
              电影频道
            </Link>
            <Link
              to="/news"
              className="whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-xs text-foreground-700 transition-colors hover:bg-background-100"
            >
              影视资讯
            </Link>
          </div>
        </div>
      </section>

      <section className="w-full border-t border-background-200 bg-background-100/60 px-4 py-10 md:px-8 md:py-12">
        <div className="mb-5 flex items-end justify-between gap-3">
          <div>
            <h2 className="font-heading text-xl font-extrabold text-foreground-950 md:text-2xl">
              也许你想看这些
            </h2>
            <p className="mt-1.5 text-xs text-foreground-500">站内热度较高的影视作品。</p>
          </div>
          <Link to="/category/movie" className="whitespace-nowrap text-xs text-foreground-600 hover:text-primary-700">
            浏览全部
            <i className="ri-arrow-right-line ml-1" />
          </Link>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 md:gap-5 lg:grid-cols-6">
          {hotMovies(6).map((movie) => (
            <MovieCard key={movie.id} movie={movie} />
          ))}
        </div>
      </section>
    </SiteShell>
  );
}